import { graphql } from 'gatsby';
import { RichText } from 'prismic-reactjs';
import * as React from 'react';

import PrimaryLayout from '../layouts/PrimaryLayout';
import PageIntro from '../components/layout/PageIntro';
import PropertyPanels from '../components/layout/PropertyPanels';
import { IPrismicComponentNode } from '../components/utils/declarations';
import htmlSerializer from '../components/layout/Prismic/PrismicRender';
import {
  prismicComponentList,
  prismicComponentDemos,
} from '../components/layout/Prismic/prismictemplateparts';
import usePrismicComponentBody, {
  IPrismicComponentBody,
} from '../components/hooks/usePrismicComponentBody';
import usePrismicComponentHeadings from '../components/hooks/usePrismicComponentHeadings';
import { useSubnav } from '../components/providers/SubnavProvider';

interface IReactTemplateProps {
  data: {
    prismic: {
      allReacts: {
        edges: [
          {
            node: IPrismicComponentNode;
          },
        ];
      };
    };
  };
}

const ReactTemplate: React.FunctionComponent<IReactTemplateProps> = ({ data }) => {
  // Required check for no data being returned
  const doc = data.prismic.allReacts.edges.slice(0, 1).pop();
  if (!doc) {
    return null;
  }

  const { updateSubnav } = useSubnav();

  const { title, intro, expanded_intro, body } = doc.node;
  const { uid, tags } = doc.node._meta;
  const category = tags.length ? tags[0] : 'notag';
  const slug = `/react/components/${category}/${uid}`;

  React.useEffect(() => {
    const demos: IPrismicComponentBody[] = usePrismicComponentBody(body, 'component_demo') || [];
    const subnavItems = usePrismicComponentHeadings(demos);
    updateSubnav(subnavItems);
  }, []);

  const pageTitle = title && RichText.asText(title);
  const components = body && prismicComponentList(body);

  return (
    <PrimaryLayout title={pageTitle || 'Prismic Preview'} path={slug}>
      <PageIntro
        title={pageTitle || 'Prismic Preview'}
        intro={intro ? RichText.asText(intro) : undefined}
        path={slug}
      />

      {expanded_intro && (
        <div>
          <RichText render={expanded_intro} htmlSerializer={htmlSerializer} />
        </div>
      )}

      {body && prismicComponentDemos(body)}

      {components && (
        <>
          <PropertyPanels components={components} platform="react" />
        </>
      )}
    </PrimaryLayout>
  );
};

export default ReactTemplate;

export const query = graphql`
  query ReactTemplateQuery($uid: String, $tag: [String!]) {
    prismic {
      allReacts(uid: $uid, tags: $tag) {
        edges {
          node {
            title
            intro
            expanded_intro
            body {
              ... on PRISMIC_ReactBodyComponent_list {
                type
                fields {
                  component
                }
              }
              ... on PRISMIC_ReactBodyComponent_demo {
                type
                primary {
                  demo_description
                  demo_title
                }
                fields {
                  demo_code
                  demo_summary
                }
              }
            }
            _meta {
              uid
              tags
            }
          }
        }
      }
    }
  }
`;
